import React, {useEffect, useRef, useState} from "react";
import {Alert, Box, Button, CircularProgress, Drawer, IconButton, InputBase, Stack, Tooltip, Typography} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {
  AiChat02Icon, ArrowRight02Icon, Bug01Icon, Cancel01Icon, Refresh01Icon, SendIcon, SmartPhone01Icon
} from "@hugeicons/core-free-icons";
import moment from "moment";
import {ConversationRepository} from "../../repositories/ConversationRepository";
import {useStompWebsocket} from "../../hooks/useStompWebsocket";
import {SupervisionHistoryItem} from "../../pages/project/supervision-page/components/supervision-history-item";
import {StatusBadge} from "../../pages/registry-project/dashboard/components/conversationUi";

const SENDER_KEY = "sender";

// Same look as the dashboard panels and the supervision detail
const roundButtonSx = {
  width: 32, height: 32, borderRadius: "50% !important", border: "1px solid #ECECF3", color: "#4A4A63", bgcolor: "#fff",
  "&:hover": {bgcolor: "#F4F4FA"},
};

const activeRoundSx = {color: "#4B55C9", bgcolor: "rgba(132, 145, 247, 0.12)", borderColor: "rgba(132, 145, 247, 0.32)"};

const fieldSx = {
  display: "flex", alignItems: "center", gap: 1, px: 1.5, borderRadius: "12px", border: "1px solid #E4E4EE",
  bgcolor: "#fff", transition: "border-color 120ms, box-shadow 120ms",
  "&:hover": {borderColor: "#D6D6E4"},
  "&:focus-within": {borderColor: "#8491F7", boxShadow: "0 0 0 3px rgba(132, 145, 247, 0.16)"},
};

const darkButtonSx = {
  color: "#fff", bgcolor: "#23264D", "&:hover": {bgcolor: "#33376B"},
  "&.Mui-disabled": {color: "rgba(255, 255, 255, 0.9)", bgcolor: "#C4C5D6"},
};

const readStorage = (key) => {
  try {
    return localStorage.getItem(key) || "";
  } catch (e) {
    return "";
  }
};

const writeStorage = (key, value) => {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch (e) {
    // storage unavailable, the number is just not remembered
  }
};

// Chat with a TEST conversation of the selected language's current flow.
// On open it starts a conversation with the stored sender number, or asks for one first.
export const TestChatDrawer = ({open, onClose, project, subProject}) => {
  const [sender, setSender] = useState(readStorage(SENDER_KEY));
  const [conversation, setConversation] = useState(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [showDebug, setShowDebug] = useState(() => readStorage("showDebug") === "true");
  const inputRef = useRef(null);
  const bottomRef = useRef(null);
  const abortRef = useRef(null);

  const [updatedValue] = useStompWebsocket(conversation?.id ? `/topic/session/${conversation.id}` : null);

  const flowUid = subProject?.flowUid;
  const languageName = subProject?.languageName || subProject?.languageCode;

  const start = async (senderNumber) => {
    if (!flowUid) {
      setError("This language has no flow to test");
      return;
    }
    setStarting(true);
    setError(null);
    setConversation(null);
    setMessages([]);
    try {
      const response = await ConversationRepository.createConversation({
        flowUid,
        sender: senderNumber,
        conversationType: "TEST",
        json: `${process.env.REACT_APP_FIREBASE_FUNCTIONS_URL}/api/apiCurrentProject/${flowUid}`,
        initParams: {
          PROJECT_VERSION_TYPE: "TEST",
          PROJECT_ID: String(project?.id),
          SUB_PROJECT_ID: String(subProject?.id),
        },
      });
      const created = response?.data;
      if (!created?.id || created.status === "FAILED") {
        setError("The test conversation could not be started. Check if the flow is correct.");
        return;
      }
      setConversation(created);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.message || "Failed to start a test conversation");
    } finally {
      setStarting(false);
    }
  };

  // Every open starts a fresh conversation when a sender number is already saved
  useEffect(() => {
    if (!open) return;
    const saved = readStorage(SENDER_KEY);
    setSender(saved);
    if (saved) start(saved);
    else {
      setConversation(null);
      setError(null);
    }
  }, [open, flowUid]);

  const fetchMessages = () => {
    if (!conversation?.id) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    ConversationRepository.getMessages(conversation.id, controller.signal)
      .then(response => setMessages([...(response?.data || [])].sort((a, b) => a.id - b.id)))
      .catch(e => {
        if (e?.name !== "AbortError" && e?.name !== "CanceledError") console.log("Failed to fetch messages:", e);
      });
  };

  useEffect(() => {
    fetchMessages();
  }, [conversation?.id, updatedValue]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({behavior: "smooth", block: "end"});
  }, [messages.length, showDebug]);

  useEffect(() => {
    if (conversation?.id && !starting) setTimeout(() => inputRef.current?.focus(), 0);
  }, [conversation?.id, starting]);

  const submitSender = (e) => {
    e?.preventDefault();
    const value = sender.trim();
    if (!value) return;
    writeStorage(SENDER_KEY, value);
    start(value);
  };

  const changeNumber = () => {
    writeStorage(SENDER_KEY, "");
    setSender(conversation?.sender || sender);
    setConversation(null);
    setMessages([]);
    setError(null);
  };

  const send = async (e) => {
    e?.preventDefault();
    const text = message.trim();
    if (!text || !conversation?.id || sending) return;
    setSending(true);
    setMessage("");
    try {
      const response = await ConversationRepository.sendMessage({id: conversation.id, message: text, flowUid: conversation.flowUid || flowUid});
      if (response?.data?.id) setConversation(prev => ({...prev, ...response.data}));
      fetchMessages();
    } catch (err) {
      console.error(err);
      setMessage(text);
      setError(err?.response?.data?.message || "Failed to send the message");
    } finally {
      setSending(false);
      // the input stays enabled while sending, so focus is kept for the next message
      inputRef.current?.focus();
    }
  };

  const toggleDebug = () => setShowDebug(prev => {
    writeStorage("showDebug", JSON.stringify(!prev));
    return !prev;
  });

  let content;
  if (starting) {
    content = (
      <Stack alignItems={"center"} justifyContent={"center"} gap={1.5} sx={{flex: 1, p: 3, bgcolor: "#FAFAFD"}}>
        <CircularProgress size={30} thickness={5} sx={{color: "#8491F7"}}/>
        <Typography sx={{fontSize: 13, color: "#8A8AA0", textAlign: "center"}}>Starting the test conversation…</Typography>
      </Stack>
    );
  } else if (!conversation?.id) {
    content = (
      <Stack component={"form"} onSubmit={submitSender} justifyContent={"center"} sx={{flex: 1, p: 2.5, bgcolor: "#FAFAFD"}}>
        <Box sx={{p: 3, bgcolor: "#fff", border: "1px solid #ECECF3", borderRadius: "18px", boxShadow: "0 1px 2px rgba(35, 38, 77, 0.04)"}}>
          <Box sx={{
            width: 44, height: 44, borderRadius: "14px", display: "flex", alignItems: "center", justifyContent: "center",
            color: "#4B55C9", bgcolor: "rgba(132, 145, 247, 0.14)", mb: 2,
          }}>
            <HugeiconsIcon icon={SmartPhone01Icon} size={22}/>
          </Box>
          <Typography sx={{fontSize: 18, fontWeight: 700, color: "#23264D", letterSpacing: "-0.01em"}}>Sender number</Typography>
          <Typography sx={{mt: 0.5, fontSize: 13, color: "#8A8AA0", lineHeight: 1.5}}>
            The test conversation runs as this number. Any valid or invalid number works for testing your scenario,
            and it is remembered for your next tests.
          </Typography>
          <Box sx={{...fieldSx, mt: 2.5, height: 42}}>
            <HugeiconsIcon icon={SmartPhone01Icon} size={16} color={"#8A8AA0"}/>
            <InputBase
              autoFocus
              fullWidth
              placeholder={"e.g. +38970123456"}
              value={sender}
              onChange={(e) => setSender(e.target.value)}
              sx={{fontSize: 14, color: "#23264D"}}
            />
          </Box>
          {error && <Alert severity={"error"} sx={{mt: 1.5, borderRadius: "12px", fontSize: 13}}>{error}</Alert>}
          <Button
            type={"submit"}
            fullWidth
            disabled={!sender.trim() || !flowUid}
            endIcon={<HugeiconsIcon icon={ArrowRight02Icon} size={16}/>}
            sx={{...darkButtonSx, mt: 2, height: 40, borderRadius: "10px", textTransform: "none", fontSize: 14, fontWeight: 600}}
          >
            Start conversation
          </Button>
        </Box>
      </Stack>
    );
  } else {
    content = (
      <>
        <Box sx={{flex: 1, minHeight: 0, overflowY: "auto", py: 1, bgcolor: "#FAFAFD"}}>
          <SupervisionHistoryItem
            conversation={conversation}
            messages={messages}
            project={project}
            loadDuration={false}
            showDebug={showDebug}
            showUserTalking={true}
            updateConversation={(updated) => setConversation(prev => ({...prev, ...updated}))}
            fetchMessages={fetchMessages}
          />
          <Box ref={bottomRef} sx={{height: 8}}/>
        </Box>
        <Box sx={{px: 2, pt: 1.5, pb: 1.25, bgcolor: "#fff", borderTop: "1px solid #F0F0F6", flexShrink: 0}}>
          {error && <Alert severity={"error"} onClose={() => setError(null)}
                           sx={{mb: 1.25, borderRadius: "12px", fontSize: 13}}>{error}</Alert>}
          <Box component={"form"} onSubmit={send} sx={{...fieldSx, alignItems: "flex-end", py: 0.75, pl: 1.75, pr: 0.75}}>
            <InputBase
              inputRef={inputRef}
              placeholder={"Write a message…"}
              multiline
              maxRows={5}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              sx={{flex: 1, py: 0.75, fontSize: 14, color: "#23264D"}}
            />
            <IconButton type={"submit"} disabled={!message.trim() || sending} aria-label={"Send"}
                        sx={{...darkButtonSx, width: 34, height: 34, borderRadius: "10px", flexShrink: 0}}>
              {sending ? <CircularProgress size={14} sx={{color: "#fff"}}/> : <HugeiconsIcon icon={SendIcon} size={16}/>}
            </IconButton>
          </Box>
          <Typography sx={{mt: 0.75, fontSize: 11, color: "#B4B4C3", textAlign: "center"}}>
            Enter to send · Shift + Enter for a new line
          </Typography>
        </Box>
      </>
    );
  }

  return (
    // persistent: no backdrop, so the page on the left stays scrollable and clickable while chatting
    <Drawer anchor={"right"} variant={"persistent"} open={open}
            PaperProps={{sx: {
              maxWidth: {xs: "calc(100% - 24px)", sm: 480}, width: "100%", top: "auto", bottom: 12, right: 12, height: "calc(100% - 74px)",
              display: "flex", flexDirection: "column", overflow: "hidden",
              border: "1px solid #ECECF3", borderRadius: "18px", boxShadow: "0 24px 60px rgba(35, 38, 77, 0.18)",
              zIndex: (theme) => theme.zIndex.drawer,
            }}}>
      <Stack direction={"row"} alignItems={"center"} gap={1.25}
             sx={{px: 2, py: 1.75, borderBottom: "1px solid #F0F0F6", flexShrink: 0}}>
        <Box sx={{
          width: 40, height: 40, borderRadius: "50%", flexShrink: 0, display: "flex", alignItems: "center",
          justifyContent: "center", color: "#4B55C9", bgcolor: "#EEF0FE",
        }}>
          <HugeiconsIcon icon={AiChat02Icon} size={20}/>
        </Box>
        <Box sx={{flex: 1, minWidth: 0}}>
          <Stack direction={"row"} alignItems={"center"} gap={0.75}>
            <Typography noWrap sx={{fontSize: 16, fontWeight: 700, color: "#23264D", letterSpacing: "-0.01em"}}>
              Test chat
            </Typography>
            <Box component={"span"} sx={{
              px: 0.875, py: 0.125, borderRadius: "999px", fontSize: 10, fontWeight: 700, letterSpacing: "0.08em",
              color: "#A86A0C", bgcolor: "#FDF4E3", flexShrink: 0,
            }}>
              TEST
            </Box>
            {conversation?.id && <StatusBadge conversation={conversation}/>}
          </Stack>
          <Typography noWrap sx={{mt: 0.25, fontSize: 12, color: "#8A8AA0"}}>
            {[
              languageName,
              conversation?.sender,
              conversation?.id && `#${conversation.id}`,
              conversation?.dateCreated && moment(conversation.dateCreated).format("HH:mm"),
            ].filter(Boolean).join(" · ") || "Current flow"}
          </Typography>
        </Box>
        {conversation?.id && <>
          <Tooltip title={showDebug ? "Hide debug messages" : "Show debug messages"}>
            <IconButton onClick={toggleDebug} sx={{...roundButtonSx, ...(showDebug ? activeRoundSx : {})}}>
              <HugeiconsIcon icon={Bug01Icon} size={15}/>
            </IconButton>
          </Tooltip>
          <Tooltip title={"Change sender number"}>
            <IconButton onClick={changeNumber} sx={roundButtonSx}>
              <HugeiconsIcon icon={SmartPhone01Icon} size={15}/>
            </IconButton>
          </Tooltip>
          <Tooltip title={"Start again"}>
            <IconButton onClick={() => start(conversation.sender)} sx={roundButtonSx}>
              <HugeiconsIcon icon={Refresh01Icon} size={15}/>
            </IconButton>
          </Tooltip>
        </>}
        <Tooltip title={"Close"}>
          <IconButton onClick={onClose} aria-label={"Close"} sx={{...roundButtonSx, border: 0}}>
            <HugeiconsIcon icon={Cancel01Icon} size={17}/>
          </IconButton>
        </Tooltip>
      </Stack>
      {content}
    </Drawer>
  );
};
